import { Application, Assets, Container } from 'pixi.js';
import { KITCHEN_TABLE, PROTOCOL_VERSION, prepareTrack, type CarSlot, type Scores, type ServerMessage } from '@nanocars/shared';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, SceneManager, type Scene } from './scenes/SceneManager';
import { TitleScene } from './scenes/TitleScene';
import { JoinScene } from './scenes/JoinScene';
import { LobbyScene } from './scenes/LobbyScene';
import { session, wsUrl } from './state/Session';
import { COLORS, colourForSlot } from './ui/theme';
import { TrackRenderer } from './render/TrackRenderer';
import { CarRenderer } from './render/CarRenderer';
import { Hud, type ChevronWarning } from './render/Hud';
import type { LiverySlot } from './assets/manifest';

async function main(): Promise<void> {
  // Texture decoding in a Web Worker stalls under headless Chrome's virtual
  // time budget, which our screenshot automation relies on. Main-thread decode
  // is fine for this asset volume.
  Assets.setPreferences({ preferWorkers: false, preferCreateImageBitmap: false });
  const app = new Application();
  await app.init({ resizeTo: window, background: COLORS.background, antialias: true });
  document.body.appendChild(app.canvas);

  const scenes = new SceneManager(app);

  // Connection failure here (e.g. no server running yet) is non-fatal:
  // TitleScene shows "Connecting..." and retries every 2s until it lands.
  session.net.connect(wsUrl()).catch((err: unknown) => {
    console.warn('NetClient: initial connect failed', err);
  });

  window.addEventListener('beforeunload', () => {
    session.input.dispose();
    session.net.disconnect();
  });

  // Automation query flags, for the headless-Chrome screenshot recipe and
  // future e2e runs: `?scene=title|join` opens a scene directly,
  // `?auto=create&name=X` drives Title straight into the create-room flow,
  // and `?auto=solo&fast=1` additionally shrinks the race so it finishes in
  // seconds (see runAutoSolo).
  const params = new URLSearchParams(location.search);
  const sceneParam = params.get('scene');
  const autoParam = params.get('auto');
  const nameParam = params.get('name');
  const fastParam = params.get('fast') === '1';

  if (sceneParam === 'join') {
    await scenes.go(new JoinScene(scenes));
    return;
  }

  // No-network render check for RaceScene's rendering pipeline (track,
  // props, cars) when a live server round-trip isn't available or isn't
  // usable for automated capture - see RacePreviewScene's own comment. It
  // draws entirely fabricated cars, scores and chevrons, so it is gated to
  // dev builds: a production visitor must never be able to reach a screen
  // showing fake race data.
  if (sceneParam === 'race-preview' && import.meta.env.DEV) {
    await scenes.go(new RacePreviewScene(app));
    return;
  }

  if (nameParam) session.name = nameParam;

  if (autoParam === 'solo') {
    await runAutoSolo(scenes, fastParam);
    return;
  }

  const title = new TitleScene(scenes);
  await scenes.go(title);
  if (autoParam === 'create') title.autoCreate();
}

function waitForConnection(): Promise<void> {
  return new Promise((resolve) => {
    const check = (): void => {
      if (session.net.connected) resolve();
      else setTimeout(check, 200);
    };
    check();
  });
}

function waitForMessage<T extends ServerMessage['t']>(t: T): Promise<Extract<ServerMessage, { t: T }>> {
  return new Promise((resolve) => {
    const unsub = session.net.on(t, (msg) => {
      unsub();
      resolve(msg);
    });
  });
}

// Drives the `?auto=solo` automation flag used by the headless-screenshot
// recipe: creates a room with the stored name, jumps straight to Lobby
// (never mounting TitleScene, so there is only ever one `welcome`/`raceStart`
// subscriber - TitleScene has its own that would otherwise race this one),
// sets 2 AI racers at normal difficulty, and starts the race. Awaiting
// `scenes.go(LobbyScene)` before sending setAi/start matters: LobbyScene's
// `raceStart` listener is only registered once its enter() (which awaits
// asset loads) resolves, and a raceStart that arrives before that would
// otherwise be silently dropped.
//
// `fast`, from `?fast=1`: sends a dev-only `devConfig` override (server
// ignores it outside `NODE_ENV !== 'production'` - see room.ts
// handleDevConfig) that drops the round to first-to-1-point, one lap,
// before starting. The automated screenshot flow has no human driving, so
// a normal 3-lap/8-point race would sit in `racing` for minutes waiting on
// the AI alone; this lets a `results`-scene screenshot land in seconds.
// Must be sent before `start` - the server only honours devConfig in the
// 'lobby' phase's RaceConfig, which startRace() then copies into the race.
async function runAutoSolo(scenes: SceneManager, fast: boolean): Promise<void> {
  await waitForConnection();

  const welcomePromise = waitForMessage('welcome');
  session.net.send({ t: 'create', v: PROTOCOL_VERSION, name: session.name });
  const welcome = await welcomePromise;

  session.playerId = welcome.playerId;
  session.net.session = welcome.session;
  session.lobby = welcome.lobby;

  await scenes.go(new LobbyScene(scenes));
  session.net.send({ t: 'setAi', count: 2, difficulty: 'normal' });
  if (fast) session.net.send({ t: 'devConfig', cfg: { pointCap: 1, lapLimit: 1 } });
  session.net.send({ t: 'start' });
}

// `?scene=race-preview`: renders the real track, real props and six parked
// cars from KITCHEN_TABLE using the actual render classes RaceScene uses
// (TrackRenderer, CarRenderer) with zero network involved. Exists for two
// reasons: (1) the brief's documented fallback for when no server is
// running yet, and (2) headless Chrome's `--virtual-time-budget` never
// delivers a WebSocket `open` event (verified: the Title screen's own
// asset-loaded UI renders fine under the same flags, but stays on
// "Connecting..." forever even though a plain `ws` client against the same
// server connects instantly) - so a live `?auto=solo` screenshot cannot be
// captured by this project's headless recipe regardless of RaceScene's own
// correctness, and this is the honest substitute for verifying the render
// stack by eye. Implemented as a real Scene (not added straight to
// app.stage) so it goes through SceneManager's letterbox root - skipping
// that the first time round left the whole render scaled and positioned
// wrong.
class RacePreviewScene implements Scene {
  readonly name = 'race-preview';
  readonly container = new Container();

  constructor(private readonly app: Application) {}

  async enter(): Promise<void> {
    const track = prepareTrack(KITCHEN_TABLE);
    const boundsW = track.bounds.max.x - track.bounds.min.x;
    const boundsH = track.bounds.max.y - track.bounds.min.y;
    const pxPerM = Math.min(LOGICAL_WIDTH / boundsW, LOGICAL_HEIGHT / boundsH);

    const world = new Container();
    world.scale.set(pxPerM);
    world.x = (LOGICAL_WIDTH - boundsW * pxPerM) / 2 - track.bounds.min.x * pxPerM;
    world.y = (LOGICAL_HEIGHT - boundsH * pxPerM) / 2 - track.bounds.min.y * pxPerM;

    const trackRenderer = new TrackRenderer(this.app, track);
    await trackRenderer.build();
    const carRenderer = new CarRenderer();

    world.addChild(trackRenderer.below, carRenderer.shadowLayer, carRenderer.carLayer, trackRenderer.above);

    for (let i = 0; i < track.startGrid.length; i++) {
      const liverySlot = (i % 6) as LiverySlot;
      await carRenderer.ensureCar(i, liverySlot);
      const grid = track.startGrid[i]!;
      carRenderer.update(i, { pos: grid.pos, heading: grid.heading, air: 0 });
    }

    this.container.addChild(world);

    // Also exercise Hud with representative sample data - race-preview has
    // no live snapshot to draw it from otherwise, and it's otherwise
    // entirely unverified by eye.
    const fakeSlots: CarSlot[] = [
      { id: 0, name: 'Martin', colour: 0, kind: 'human' },
      { id: 1, name: 'Bot Red', colour: 1, kind: 'ai', difficulty: 'normal' },
      { id: 2, name: 'Bot Yellow', colour: 2, kind: 'ai', difficulty: 'normal' },
      { id: 3, name: 'Bot Green', colour: 3, kind: 'ai', difficulty: 'easy' },
    ];
    const fakeScores: Scores = { 0: 3, 1: 2, 2: 1, 3: 0 };
    const fakeChevrons: ChevronWarning[] = [
      { colour: colourForSlot(1), screenPos: { x: LOGICAL_WIDTH - 40, y: 200 }, angle: 0, flashing: true },
      { colour: colourForSlot(2), screenPos: { x: 40, y: LOGICAL_HEIGHT - 120 }, angle: Math.PI, flashing: false },
    ];

    const hud = new Hud();
    // dt=0.1 lands the flash clock mid-cycle (visibly on) rather than at its
    // t=0 start (sin(0)=0, i.e. hidden) - a static screenshot needs a single
    // update call to land somewhere the flashing elements are actually shown.
    hud.update(
      {
        slots: fakeSlots,
        scores: fakeScores,
        phase: 'racing',
        phaseTime: 5,
        countdownLength: 3,
        roundWinner: null,
        ownLap: 1,
        lapLimit: 3,
        wrongWay: true,
        chevrons: fakeChevrons,
      },
      0.1,
    );
    this.container.addChild(hud.container);
  }

  exit(): void {
    this.container.removeChildren();
  }

  update(_dt: number): void {
    // Static preview; nothing to animate.
  }

  resize(_w: number, _h: number): void {
    // Fixed 1280x720 logical layout.
  }
}

void main();
